/**
 * 👨‍👩‍👧 **어른 확인** — 자유 대화를 열기 전에 옆에 어른이 있는지 묻는다.
 *
 * ## 무엇을 지키려는 게 아닌가
 * 이건 **보안 장치가 아니다.** 마음먹은 사람은 계산기를 쓰면 된다.
 * 지키려는 건 하나다 — **혼자 있는 0~7세가 실수로 열지 않게 하는 것.**
 * 그 나이 아이는 두 자리 곱셈을 못 하고, 대개 시도조차 하지 않는다.
 *
 * ## 왜 필요한가
 * 기획 §4.3 의 1차 타깃은 0~7세 자녀를 둔 보호자다. 자유 대화의 실제 사용자는
 * **옆에 있는 어른**이다(아이는 글을 읽지도 타이핑도 못 한다). 그러니 이 칸은
 * 「아이를 막는 기능」이 아니라 **어른용 창구를 여는 문**이다.
 *
 * 2026 년 미국 하원을 통과한 아동 온라인 안전 법안 묶음과 논의 중인 챗봇 법안이
 * 13세 미만 대화형 AI 에 **보호자 동의**를 요구하는 방향이다(docs/build/00 S4-4).
 *
 * ## 기억하지 않는다
 * 통과는 **그 대화창 한 번**만 유효하다. 저장하면 아이가 나중에 혼자 열 수 있고,
 * 그건 이 문을 만든 이유를 없앤다. 저장할 곳도 없다(무수집).
 */

/** 문제 하나. 답은 화면이 갖고 있지 않다 — 맞았는지는 `adultGateOk` 가 판정한다. */
export interface AdultQuiz {
  a: number;
  b: number;
  /** 화면에 그대로 띄울 물음. */
  text: string;
}

/** 두 자리 곱셈 — 0~7세가 암산으로 못 하고, 어른은 2~3초면 한다. */
export function makeAdultQuiz(rand: () => number = Math.random): AdultQuiz {
  // 11~19 × 3~9. 십의 자리가 1 이라 어른에게는 쉽고, 아이에게는 자릿수가 넘어간다.
  const a = 11 + Math.floor(rand() * 9);
  const b = 3 + Math.floor(rand() * 7);
  return { a, b, text: `${a} × ${b} = ?` };
}

/**
 * 틀렸을 때 낼 **다른** 문제. 🔴 그냥 새로 뽑으면 63가지 중 하나라 1/63 로 같은 문제가 다시 나온다 —
 * 「틀리면 문제가 바뀐다(계속 찍지 못하게)」는 약속이 운에 걸려 있었다(2026-10-04 단위검사가 우연히 잡았다).
 */
export function makeOtherAdultQuiz(prev: AdultQuiz, rand: () => number = Math.random): AdultQuiz {
  const next = makeAdultQuiz(rand);
  if (next.a !== prev.a || next.b !== prev.b) return next;
  // 같으면 곱하는 수만 한 칸 옮긴다(3~9 안에서 돈다) — 다시 뽑기를 되풀이하지 않아 늘 한 번에 끝난다
  const b = next.b >= 9 ? 3 : next.b + 1;
  return { a: next.a, b, text: `${next.a} × ${b} = ?` };
}

/**
 * 답이 맞나. 공백·전각 숫자를 받아 준다 — 어른이 폰에서 치다가 틀린 것으로 보이면 안 된다.
 * (자릿수 구분 쉼표도 받는다. 「132」와 「1 3 2」는 같은 답이다.)
 */
export function adultGateOk(quiz: AdultQuiz, input: string): boolean {
  if (typeof input !== "string") return false;
  const normalized = input
    .replace(/[０-９]/g, (d) => String(d.charCodeAt(0) - 0xff10)) // 전각 숫자
    .replace(/[\s,]/g, "");
  if (!/^-?\d{1,6}$/.test(normalized)) return false;
  return Number(normalized) === quiz.a * quiz.b;
}
